import { IconButton } from "@material-ui/core";
import CreateIcon from "@material-ui/icons/Create";
import { useTranslations } from "adapters/i18n";
import { CustomTableColumn } from "features/shared/components";
import SwitchCustomizado from "features/shared/components/SwitchCustomizado";
import React, { useMemo } from "react";
import type { CauseOfDeath } from "shared/types";
import { CauseOfDeathListTableColumn } from "../utils/causeOfDeath.utils";

type UseCauseOfDeathTableProps = {
  isFormPanelOpen: boolean;
  onEditClick: (row: CauseOfDeath) => void;
  onToggleStatus: (row: CauseOfDeath, isCurrentDeleted: boolean) => void;
};

export function useCauseOfDeathTable({
  isFormPanelOpen,
  onEditClick,
  onToggleStatus,
}: UseCauseOfDeathTableProps) {
  const { t } = useTranslations();

  const columns = useMemo<
    CustomTableColumn<CauseOfDeath, typeof CauseOfDeathListTableColumn>[]
  >(
    () => [
      {
        id: CauseOfDeathListTableColumn.Name,
        label: t("livestock:cause-of-death-short"),
        isSortable: true,
        type: "text",
        getRowValue: (row) => row.name,
      },
      {
        id: CauseOfDeathListTableColumn.Actions,
        label: t("common:actions.edit"),
        isSortable: false,
        isActionColumn: true,
        type: "custom",
        renderCell: (row) => (
          <EditCell
            isFormPanelOpen={isFormPanelOpen}
            onEditClick={onEditClick}
            row={row}
          />
        ),
      },
      {
        id: CauseOfDeathListTableColumn.Status,
        label: t("common:status.active"),
        isSortable: false,
        isActionColumn: true,
        type: "custom",
        renderCell: (row) => (
          <StatusCell onToggleStatus={onToggleStatus} row={row} />
        ),
      },
    ],
    [isFormPanelOpen, onEditClick, onToggleStatus, t]
  );

  return { columns };
}

type EditCellProps = {
  isFormPanelOpen: boolean;
  onEditClick: (row: CauseOfDeath) => void;
  row: CauseOfDeath;
};

function EditCell({ isFormPanelOpen, onEditClick, row }: EditCellProps) {
  const { t } = useTranslations();

  const handleEditClick = () => {
    onEditClick(row);
  };

  return (
    <IconButton
      aria-label={t("common:actions.edit")}
      data-testid="edit-cause-of-death-button"
      disabled={isFormPanelOpen}
      onClick={handleEditClick}
    >
      <CreateIcon />
    </IconButton>
  );
}

type StatusCellProps = {
  onToggleStatus: (row: CauseOfDeath, isCurrentDeleted: boolean) => void;
  row: CauseOfDeath;
};

function StatusCell({ onToggleStatus, row }: StatusCellProps) {
  const isDeleted = row.deletedAt != null || row.deletionUser != null;

  const handleToggle = () => {
    onToggleStatus(row, isDeleted);
  };

  return (
    <SwitchCustomizado defaultChecked={!isDeleted} handleClick={handleToggle} />
  );
}
